import { Outlet, Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { motion, useMotionValueEvent, useReducedMotion, useScroll } from 'framer-motion';
import { Menu, X, LayoutDashboard, ShieldCheck } from 'lucide-react';
import { InstallMenuItem } from '@/components/pwa';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import { Logo, SiteFooter, ThemeToggle } from '@/components/ui';
import { FeedbackButton } from '@/components/ui/FeedbackButton';

function scrollToHash(hash: string) {
  const element = document.getElementById(hash.replace('#', ''));
  if (element) element.scrollIntoView({ behavior: 'smooth' });
}

const NAV_SECTIONS = [{ hash: '#como-funciona', label: 'Como funciona' }];

export function MainLayout() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const { user } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();

  const [hoveredNavKey, setHoveredNavKey] = useState<string | null>(null);
  const shouldReduceMotion = useReducedMotion() ?? false;
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, 'change', (latest) => setHasScrolled(latest > 8));
  useEffect(() => setHasScrolled(window.scrollY > 8), []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    if (location.hash) {
      const timer = window.setTimeout(() => scrollToHash(location.hash), 100);
      return () => window.clearTimeout(timer);
    }
    window.scrollTo(0, 0);
    return undefined;
  }, [location.pathname, location.hash]);

  const handleHashNavigation = (hash: string) => {
    setIsMobileMenuOpen(false);
    if (location.pathname === '/') scrollToHash(hash);
    else navigate('/' + hash);
  };

  const navLinkClass = 'relative whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium text-ink/70 transition-colors hover:text-ink';
  const activeRouteKey = location.pathname.startsWith('/explorar') ? 'explorar' : null;
  // O indicador segue o item sob o cursor e volta para a rota ativa ao sair do menu.
  const indicatorKey = hoveredNavKey ?? activeRouteKey;
  const indicatorTransition = shouldReduceMotion ? { duration: 0 } : { type: 'spring' as const, stiffness: 480, damping: 38 };

  const renderIndicator = (key: string) =>
    indicatorKey === key ? (
      <motion.span
        layoutId="main-nav-indicator"
        transition={indicatorTransition}
        className={cn('absolute inset-0 -z-10 rounded-lg', key === activeRouteKey ? 'bg-brand-400/10' : 'bg-ink/[0.05]')}
        aria-hidden="true"
      />
    ) : null;

  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      <a href="#conteudo" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-lg focus:bg-elevated focus:px-4 focus:py-2 focus:shadow-elevation-3">
        Pular para o conteúdo
      </a>

      <header
        className={cn(
          'sticky top-0 z-50 w-full border-b transition-[background-color,border-color,box-shadow] duration-300',
          hasScrolled
            ? 'border-line/80 bg-canvas/75 shadow-elevation-2 backdrop-blur-md supports-[backdrop-filter]:bg-canvas/65'
            : 'border-transparent bg-canvas'
        )}
      >
        {/* Altura fixa no layout; a compactação é só visual (transform) para não causar layout shift. */}
        <motion.nav
          animate={{ scale: hasScrolled && !shouldReduceMotion ? 0.985 : 1, y: hasScrolled && !shouldReduceMotion ? -2 : 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8" aria-label="Navegação principal">
          <Logo />

          <div className="relative isolate hidden items-center gap-1 lg:flex" onMouseLeave={() => setHoveredNavKey(null)}>
            <NavLink
              to="/explorar"
              onMouseEnter={() => setHoveredNavKey('explorar')}
              onFocus={() => setHoveredNavKey('explorar')}
              onBlur={() => setHoveredNavKey(null)}
              className={({ isActive }) => cn(navLinkClass, isActive && 'text-brand-300')}
            >
              {renderIndicator('explorar')}
              Explorar
            </NavLink>
            {[{ to: '/chamadas', label: 'Chamadas' }, { to: '/patrocinadores', label: 'Patrocinadores' }].map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onMouseEnter={() => setHoveredNavKey(item.to)}
                onFocus={() => setHoveredNavKey(item.to)}
                onBlur={() => setHoveredNavKey(null)}
                className={({ isActive }) => cn(navLinkClass, isActive && 'text-brand-300')}
              >
                {renderIndicator(item.to)}
                {item.label}
              </NavLink>
            ))}
            {NAV_SECTIONS.map((section) => (
              <button
                key={section.hash}
                type="button"
                onClick={() => handleHashNavigation(section.hash)}
                onMouseEnter={() => setHoveredNavKey(section.hash)}
                onFocus={() => setHoveredNavKey(section.hash)}
                onBlur={() => setHoveredNavKey(null)}
                className={navLinkClass}
              >
                {renderIndicator(section.hash)}
                {section.label}
              </button>
            ))}
          </div>

          <div className="hidden shrink-0 items-center gap-1.5 lg:flex">
            <ThemeToggle />
            {user ? (
              <Link to="/dashboard" className="btn-primary whitespace-nowrap px-4 py-2">
                <LayoutDashboard className="h-4 w-4" aria-hidden="true" />
                Meu painel
              </Link>
            ) : (
              <>
                <Link to="/login" className="btn-ghost whitespace-nowrap px-4 py-2">
                  Entrar
                </Link>
                <Link to="/cadastro" className="btn-primary whitespace-nowrap px-4 py-2">
                  Criar conta
                </Link>
              </>
            )}
          </div>

          <div className="flex items-center gap-1 lg:hidden">
            {!user && (
              <>
                <Link to="/login" className="btn-ghost hidden whitespace-nowrap px-3 py-1.5 text-xs min-[400px]:inline-flex">
                  Entrar
                </Link>
                <Link to="/cadastro" className="btn-primary whitespace-nowrap px-3 py-1.5 text-xs">
                  Criar conta
                </Link>
              </>
            )}
            <ThemeToggle />
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-ink transition-colors hover:bg-ink/[0.06]"
              onClick={() => setIsMobileMenuOpen((open) => !open)}
              aria-label={isMobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={isMobileMenuOpen}
              aria-controls="menu-mobile"
            >
              {isMobileMenuOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
            </button>
          </div>
        </motion.nav>

        {isMobileMenuOpen && (
          <div id="menu-mobile" className="animate-slide-down border-t border-line bg-elevated px-4 pb-6 pt-3 shadow-elevation-3 lg:hidden">
            <div className="flex flex-col gap-1">
              <Link to="/explorar" className="rounded-lg px-3 py-3 text-sm font-medium text-ink hover:bg-ink/[0.05]">
                Explorar
              </Link>
              <Link to="/chamadas" className="rounded-lg px-3 py-3 text-sm font-medium text-ink hover:bg-ink/[0.05]">
                Chamadas de patrocínio
              </Link>
              <Link to="/patrocinadores" className="rounded-lg px-3 py-3 text-sm font-medium text-ink hover:bg-ink/[0.05]">
                Patrocinadores
              </Link>
              {NAV_SECTIONS.map((section) => (
                <button
                  key={section.hash}
                  type="button"
                  onClick={() => handleHashNavigation(section.hash)}
                  className="rounded-lg px-3 py-3 text-left text-sm font-medium text-ink hover:bg-ink/[0.05]"
                >
                  {section.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => handleHashNavigation('#seguranca')}
                className="rounded-lg px-3 py-3 text-left text-sm font-medium text-ink hover:bg-ink/[0.05]"
              >
                Segurança
              </button>
              <Link to="/faq" className="rounded-lg px-3 py-3 text-sm font-medium text-ink hover:bg-ink/[0.05]">
                Dúvidas
              </Link>
              <InstallMenuItem className="py-3 text-ink" />
              <div className="my-3 border-t border-line" />
              {user ? (
                <Link to="/dashboard" className="btn-primary">
                  Meu painel
                </Link>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Link to="/login" className="btn-secondary">
                    Entrar
                  </Link>
                  <Link to="/cadastro" className="btn-primary">
                    Criar conta
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      <main id="conteudo" className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-line bg-elevated/60">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr] lg:px-8">
          <div>
            <Logo />
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-muted">
              Conectamos clubes, atletas e eventos a empresas patrocinadoras. O acordo e o pagamento ficam entre as partes.
            </p>
            <p className="mt-4 inline-flex items-center gap-2 text-xs font-medium text-verified">
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              Perfis públicos só com KYC aprovado
            </p>
          </div>

          <div>
            <h2 className="font-sans text-xs font-semibold uppercase tracking-wider text-muted">Plataforma</h2>
            <ul className="mt-4 space-y-3 text-sm">
              <li><Link to="/explorar" className="text-ink/75 hover:text-ink">Explorar oportunidades</Link></li>
              <li><button type="button" onClick={() => handleHashNavigation('#como-funciona')} className="text-ink/75 hover:text-ink">Como funciona</button></li>
              <li><button type="button" onClick={() => handleHashNavigation('#seguranca')} className="text-ink/75 hover:text-ink">Segurança e transparência</button></li>
              <li><Link to="/planos" className="text-ink/75 hover:text-ink">Planos</Link></li>
            </ul>
          </div>

          <div>
            <h2 className="font-sans text-xs font-semibold uppercase tracking-wider text-muted">Ajuda</h2>
            <ul className="mt-4 space-y-3 text-sm">
              <li><Link to="/ajuda" className="text-ink/75 hover:text-ink">Central de ajuda</Link></li>
              <li><Link to="/faq" className="text-ink/75 hover:text-ink">Perguntas frequentes</Link></li>
              <li><Link to="/contato" className="text-ink/75 hover:text-ink">Contato</Link></li>
            </ul>
          </div>

          <div>
            <h2 className="font-sans text-xs font-semibold uppercase tracking-wider text-muted">Legal</h2>
            <ul className="mt-4 space-y-3 text-sm">
              <li><Link to="/termos" className="text-ink/75 hover:text-ink">Termos de Uso</Link></li>
              <li><Link to="/privacidade" className="text-ink/75 hover:text-ink">Política de Privacidade</Link></li>
            </ul>
          </div>
        </div>
        <SiteFooter />
      </footer>

      {/* Ponto de montagem para ações flutuantes (ex.: botão "Enviar feedback"). */}
      <div id="floating-actions" className="fixed bottom-4 right-4 z-40 flex flex-col items-end gap-2 sm:bottom-6 sm:right-6">
        <div style={{ order: 10 }}>
          <FeedbackButton />
        </div>
      </div>
    </div>
  );
}
